import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar,
  Badge,
  Chip,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tab,
  Tabs,
  InputAdornment,
  Menu,
  Fade
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import apiClient from '../../request/http-common';
import { useSelector } from 'react-redux';
import { formatDistance } from 'date-fns';

// Define animations
const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
`;

// Styled components
const ChatContainer = styled(Paper)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const ChatListContainer = styled(Paper)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: '16px',
  backgroundColor: '#f9f9f9',
}));

const ChatInput = styled(Box)(({ theme }) => ({
  padding: '10px',
  borderTop: '1px solid #eee',
  display: 'flex',
  alignItems: 'center',
}));

const MessageBubble = styled(Box)(({ theme, variant, status }) => ({
  backgroundColor: variant === 'admin' 
    ? '#e17800' 
    : '#e3f2fd',
  color: variant === 'admin' 
    ? '#ffffff' 
    : '#212529',
  borderRadius: variant === 'admin'
    ? '18px 18px 0 18px'
    : '18px 18px 18px 0',
  boxShadow: variant === 'admin'
    ? '0 1px 2px rgba(0,0,0,0.1)'
    : '0 1px 2px rgba(0,0,0,0.05)',
  padding: '10px 16px',
  marginBottom: '8px',
  maxWidth: '70%',
  wordBreak: 'break-word',
  marginLeft: variant === 'admin' ? 'auto' : '0',
  marginRight: variant === 'admin' ? '0' : 'auto',
  borderLeft: variant === 'visitor' ? '3px solid #bbdefb' : 'none',
  opacity: status === 'sending' ? 0.7 : 1,
  transform: status === 'sent' ? 'translateY(0)' : (status === 'sending' ? 'translateY(5px)' : 'translateY(0)'),
  transition: 'opacity 0.3s ease, transform 0.3s ease',
}));

const SystemMessage = styled(Box)(({ theme }) => ({
  backgroundColor: '#edf2f7',
  color: '#4a5568',
  borderRadius: '10px',
  padding: '6px 12px',
  margin: '12px auto',
  fontSize: '0.85rem',
  maxWidth: '90%',
  textAlign: 'center',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  border: '1px solid #e2e8f0',
}));

const ChatListHeader = styled(Box)(({ theme }) => ({
  padding: '10px',
  borderBottom: '1px solid #eee',
}));

const MessageStatus = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginTop: '4px',
  fontSize: '11px',
}));

const PulseLoader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '3px',
  margin: '4px 0',
  '& > span': {
    width: '6px',
    height: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
    display: 'inline-block',
    animation: `${pulseAnimation} 1.5s infinite ease-in-out`,
    '&:nth-of-type(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s',
    },
  }
}));

const LiveChatAdmin = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [chats, setChats] = useState([]);
  const [filteredChats, setFilteredChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    closed: 0,
    assigned: 0,
    unassigned: 0,
    today: 0,
    last7days: 0
  });
  const [statusFilter, setStatusFilter] = useState('active');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [prevMessageCount, setPrevMessageCount] = useState(0);
  const messageSound = useRef(null);
  
  const user = useSelector((state) => state.user.user);
  const messagesEndRef = useRef(null);
  const chatPollingTime = 5000; // Reduced from 10000 to 5000 ms for quicker updates
  const activePollingTime = 3000; // Even faster polling when chat is actively selected

  // Move polling references to component level for better control
  const chatsIntervalRef = useRef(null);
  const selectedChatIntervalRef = useRef(null);

  // Add a state to track the last successful API fetch time
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const maxConsecutiveErrors = 3;

  // Initialize sound notification
  useEffect(() => {
    // Create a simple audio element for notification
    const audio = new Audio('https://res.cloudinary.com/dyz8kppso/video/upload/v1742086145/notification_bzrubx.mp3');
    audio.volume = 0.5; // Set volume to 50%
    messageSound.current = audio;
    
    return () => {
      if (messageSound.current) {
        messageSound.current.pause();
        messageSound.current = null;
      }
    };
  }, []);

  useEffect(() => {
    loadChats();
    loadStatistics();
    loadAdmins();

    // Setup polling for chats list (less frequent)
    chatsIntervalRef.current = setInterval(() => {
      loadChats();
      loadStatistics();
    }, chatPollingTime);

    // Cleanup both intervals on unmount
    return () => {
      if (chatsIntervalRef.current) clearInterval(chatsIntervalRef.current);
      if (selectedChatIntervalRef.current) clearInterval(selectedChatIntervalRef.current);
    };
  }, []);
  
  // Dedicated effect for selected chat polling - recreated when selected chat changes
  useEffect(() => {
    // Clear any existing selected chat polling
    if (selectedChatIntervalRef.current) {
      clearInterval(selectedChatIntervalRef.current);
    }
    
    if (selectedChat?.id) {
      console.log(`Starting polling for chat ID: ${selectedChat.id}`);
      
      // Immediately refresh on selection
      refreshSelectedChat();
      scrollToBottom();
      
      // Setup more aggressive polling for the selected chat
      selectedChatIntervalRef.current = setInterval(() => {
        if (selectedChat && selectedChat.status === 'active') {
          console.log(`Polling chat ID: ${selectedChat.id}`);
          refreshSelectedChat();
        }
      }, activePollingTime);
    }
    
    return () => {
      if (selectedChatIntervalRef.current) {
        clearInterval(selectedChatIntervalRef.current);
      }
    };
  }, [selectedChat?.id]); // Only recreate when the chat ID changes

  useEffect(() => {
    if (selectedChat) {
      // Immediately refresh on selection
      refreshSelectedChat();
      scrollToBottom();
    }
  }, [selectedChat?.id]); // Only depends on the ID changing, not the whole chat object

  // Scroll to bottom when messages change
  useEffect(() => {
    if (selectedChat?.messages?.length > 0) {
      // Allow a small delay for the Fade animation to begin
      const scrollTimeout = setTimeout(() => {
        scrollToBottom();
      }, 100);
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [selectedChat?.messages]);

  useEffect(() => {
    filterChats();
  }, [chats, search, selectedTab]);

  const filterChats = () => {
    let filtered = [...chats];
    
    // Apply tab/status filter
    if (selectedTab === 0) {
      filtered = filtered.filter(chat => chat.status === 'active');
    } else if (selectedTab === 1) {
      filtered = filtered.filter(chat => chat.status === 'closed');
    }
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(chat => 
        (chat.visitor?.name && chat.visitor.name.toLowerCase().includes(searchLower)) ||
        (chat.visitor?.email && chat.visitor.email.toLowerCase().includes(searchLower)) ||
        (chat.location && chat.location.toLowerCase().includes(searchLower)) ||
        (chat.page_url && chat.page_url.toLowerCase().includes(searchLower))
      );
    }
    
    setFilteredChats(filtered);
  };

  const loadChats = async () => {
    try {
      const response = await apiClient.get('/api/live-chat/admin/list');
      if (response.data.status === 'success') {
        setChats(response.data.chats.data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await apiClient.get('/api/live-chat/admin/statistics');
      if (response.data.status === 'success') {
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadAdmins = async () => {
    try {
      const response = await apiClient.get('/api/live-chat/admin/admin-list');
      if (response.data.status === 'success') {
        setAdmins(response.data.admins);
      }
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  };

  const refreshSelectedChat = async () => {
    if (!selectedChat || !selectedChat.id) return;
    
    console.log(`Refreshing chat ID: ${selectedChat.id}`);
    
    try {
      // Add timestamp to avoid caching issues
      const response = await apiClient.get(
        `/api/live-chat/admin/chat/${selectedChat.id}?t=${Date.now()}`
      );
      
      if (response.data.status === 'success') {
        // Reset error counter on success
        if (consecutiveErrors > 0) {
          setConsecutiveErrors(0);
        }
        
        // Record successful fetch time
        setLastFetchTime(Date.now());
        
        // Only update if there are new messages or status changes
        const currentMsgCount = selectedChat.messages?.length || 0;
        const newMsgCount = response.data.messages.length;
        
        if (newMsgCount > currentMsgCount || response.data.chat.status !== selectedChat.status) {
          console.log(`Chat updated: ${currentMsgCount} → ${newMsgCount} messages`);
          
          // If we have new messages and it's not the initial load, play notification sound
          if (newMsgCount > currentMsgCount && currentMsgCount > 0) {
            // Play sound for new visitor message only (not for our own messages)
            const hasNewVisitorMessage = response.data.messages
              .slice(currentMsgCount)
              .some(msg => msg.sender_type === 'visitor');
              
            if (hasNewVisitorMessage && messageSound.current) {
              messageSound.current.play().catch(e => console.error('Error playing notification:', e));
              
              // If browser supports notifications, show one
              if ("Notification" in window && Notification.permission === "granted") {
                const visitorName = selectedChat.visitor?.name || 'Visitor';
                new Notification(`New message from ${visitorName}`, {
                  body: response.data.messages[newMsgCount-1].message.substring(0, 50) + 
                        (response.data.messages[newMsgCount-1].message.length > 50 ? '...' : ''),
                  icon: '/favicon.ico'
                });
              }
            }
          }
          
          // Mark new messages with a visual indicator
          const updatedMessages = response.data.messages.map((msg, index) => {
            // If this message is new (beyond our current count), mark it
            const isNew = index >= currentMsgCount;
            return {
              ...msg,
              isNew: isNew // This will be used for visual feedback
            };
          });
          
          setSelectedChat(prevChat => ({
            ...response.data.chat,
            messages: updatedMessages,
            status: response.data.chat.status,
            lastUpdated: Date.now(),
            // Preserve any local UI state that might be needed
            ...prevChat && prevChat.id === response.data.chat.id 
              ? { uiState: prevChat.uiState }
              : {}
          }));
          
          // Update the previous message count for future comparisons
          setPrevMessageCount(newMsgCount);
        
        // Update this chat in the chats list
        setChats(prevChats => {
          return prevChats.map(chat => {
            if (chat.id === selectedChat.id) {
              return {
                ...chat,
                  unread_count: 0,
                  status: response.data.chat.status,
                  last_message: response.data.messages[newMsgCount-1]?.message || chat.last_message,
                  last_message_time: response.data.messages[newMsgCount-1]?.created_at || chat.last_message_time
              };
            }
            return chat;
          });
        });
          
          // Scroll to bottom when new messages arrive
          scrollToBottom();
        } else {
          // Even if no new messages, update chat status if changed
          if (response.data.chat.status !== selectedChat.status) {
            setSelectedChat(prevChat => ({
              ...prevChat,
              status: response.data.chat.status
            }));
            
            // Update status in chat list
            setChats(prevChats => {
              return prevChats.map(chat => {
                if (chat.id === selectedChat.id) {
                  return {
                    ...chat,
                    status: response.data.chat.status
                  };
                }
                return chat;
              });
            });
          } else {
            console.log('No new messages or status changes found during refresh');
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing chat:', error);
      
      // Count consecutive errors
      setConsecutiveErrors(prev => {
        const newCount = prev + 1;
        
        // If we've had several consecutive errors, try resetting the interval
        if (newCount >= maxConsecutiveErrors) {
          console.log('Too many consecutive errors, resetting polling interval');
          
          // Reset polling for this chat
          if (selectedChatIntervalRef.current) {
            clearInterval(selectedChatIntervalRef.current);
            
            // Set up a new interval after a brief pause
            setTimeout(() => {
              selectedChatIntervalRef.current = setInterval(() => {
                if (selectedChat && selectedChat.status === 'active') {
                  refreshSelectedChat();
                }
              }, activePollingTime);
            }, 3000);
          }
          
          return 0; // Reset counter
        }
        
        return newCount;
      });
    }
  };

  const handleChatSelect = async (chat) => {
    try {
      // Immediately show an empty chat to indicate selection
      setSelectedChat({ 
        ...chat, 
        messages: [],
        loading: true // Add loading state for better UX
      });
      setLoading(true);
      
      // Force clear and restart polling for the new chat
      if (selectedChatIntervalRef.current) {
        clearInterval(selectedChatIntervalRef.current);
        selectedChatIntervalRef.current = null;
      }
      
      // Get chat details and messages
      const response = await apiClient.get(`/api/live-chat/admin/chat/${chat.id}`);
      if (response.data.status === 'success') {
        // Set selected chat with full details and messages
        setSelectedChat({
          ...response.data.chat,
          messages: response.data.messages,
          loading: false // Clear loading state
        });
        
        // Update this chat in the chats list to remove unread count
        setChats(prevChats => {
          return prevChats.map(c => {
            if (c.id === chat.id) {
              return { 
                ...c, 
                unread_count: 0,
                last_checked: new Date().toISOString()
              };
            }
            return c;
          });
        });

        // Polling will be automatically set up by the useEffect that watches selectedChat?.id
      } else {
        console.error('Error in API response when selecting chat:', response.data);
        setSelectedChat({ 
          ...chat, 
          messages: [],
          loading: false,
          error: 'Failed to load chat messages' 
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error selecting chat:', error);
      setLoading(false);
      setSelectedChat({ 
        ...chat, 
        messages: [],
        loading: false,
        error: 'Failed to load chat messages' 
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || !selectedChat) return;
    
    const messageToSend = message;
    setMessage('');
    
    try {
      const response = await apiClient.post('/api/live-chat/admin/send', {
        chat_id: selectedChat.id,
        message: messageToSend
      });
      
      if (response.data.status === 'success') {
        // Update the chat messages with the new message
        setSelectedChat(prev => ({
          ...prev,
          messages: [...prev.messages, response.data.message_details]
        }));
        
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Could handle error and restore message to input
    }
  };

  const handleCloseChat = async () => {
    if (!selectedChat) return;
    
    try {
      await apiClient.post('/api/live-chat/admin/close', {
        chat_id: selectedChat.id,
        message: `Chat closed by ${user.name}`
      });
      
      // Refresh the chat to show it's closed
      refreshSelectedChat();
      
      // Refresh the chat list
      loadChats();
    } catch (error) {
      console.error('Error closing chat:', error);
    }
  };

  const handleReassignChat = async () => {
    if (!selectedChat || !selectedAdmin) return;
    
    try {
      await apiClient.post('/api/live-chat/admin/reassign', {
        chat_id: selectedChat.id,
        admin_id: selectedAdmin
      });
      
      // Refresh the chat to show new assignment
      refreshSelectedChat();
      
      // Close the dialog
      setReassignDialogOpen(false);
      setSelectedAdmin('');
    } catch (error) {
      console.error('Error reassigning chat:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openReassignDialog = () => {
    setReassignDialogOpen(true);
    handleMenuClose();
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return formatDistance(date, new Date(), { addSuffix: true });
  };

  const renderChatStatus = (status, isActive = false) => {
    // Show "updating" status for the selected chat when actively fetching
    if (isActive && selectedChat?.loading) {
      return <Chip size="small" label="Loading..." color="info" />;
    }
    
    switch(status) {
      case 'active':
        return (
          <Chip 
            size="small" 
            label={isActive ? "Updating..." : "Active"} 
            color="success" 
            sx={isActive ? { 
              '&::after': {
                content: '""',
                position: 'absolute',
                right: '-6px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#4caf50',
                animation: `${pulseAnimation} 1.5s infinite`
              }
            } : {}}
          />
        );
      case 'closed':
        return <Chip size="small" label="Closed" color="default" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  // Make loading status visible in the UI
  useEffect(() => {
    if (selectedChat) {
      // Show real-time status while loading
      const loadingTimeout = setTimeout(() => {
        if (selectedChat.loading) {
          setSelectedChat(prev => ({
            ...prev,
            loading: false
          }));
        }
      }, 3000); // Max loading time of 3 seconds
      
      return () => clearTimeout(loadingTimeout);
    }
  }, [selectedChat?.loading]);

  // Ask for notification permission when component mounts
  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" gutterBottom>Live Chat Administration</Typography>
      
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Active Chats</Typography>
              <Typography variant="h3" color="primary">{statistics.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Unassigned</Typography>
              <Typography variant="h3" color="error">{statistics.unassigned}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Today</Typography>
              <Typography variant="h3">{statistics.today}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">Last 7 Days</Typography>
              <Typography variant="h3">{statistics.last7days}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Chat List */}
        <Grid item xs={12} md={4}>
          <ChatListContainer>
            <ChatListHeader>
              <Tabs 
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="fullWidth"
              >
                <Tab label={`Active (${statistics.active})`} />
                <Tab label={`Closed (${statistics.closed})`} />
              </Tabs>
              <Box sx={{ p: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Search chats..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </ChatListHeader>
            
            <List sx={{ overflow: 'auto', flexGrow: 1 }}>
              {loading && filteredChats.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : filteredChats.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No chats found
                  </Typography>
                </Box>
              ) : (
                filteredChats.map((chat) => (
                  <ListItem 
                    key={chat.id} 
                    button 
                    selected={selectedChat?.id === chat.id}
                    onClick={() => handleChatSelect(chat)}
                    divider
                  >
                    <ListItemAvatar>
                      <Badge 
                        color="error"
                        badgeContent={chat.unread_count || 0}
                        overlap="circular"
                      >
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography noWrap>
                            {chat.visitor?.name || 'Anonymous Visitor'}
                          </Typography>
                          {renderChatStatus(chat.status, selectedChat?.id === chat.id)}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" noWrap component="span">
                            {chat.visitor?.email || chat.location || 'No details'}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(chat.created_at)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
            
            <Box sx={{ p: 1, borderTop: '1px solid #eee', textAlign: 'center' }}>
              <Button 
                startIcon={<RefreshIcon />}
                onClick={loadChats}
              >
                Refresh
              </Button>
            </Box>
          </ChatListContainer>
        </Grid>
        
        {/* Chat Window */}
        <Grid item xs={12} md={8}>
          {selectedChat ? (
            <ChatContainer>
              {/* Chat Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">
                    {selectedChat.visitor?.name || 'Anonymous Visitor'}
                    {' '}
                    {renderChatStatus(selectedChat.status, true)}
                  </Typography>
                  <Typography variant="body2">
                    {selectedChat.visitor?.email && (
                      <>Email: {selectedChat.visitor.email} · </>
                    )}
                    {selectedChat.location && (
                      <>Location: {selectedChat.location}</>
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Chat Options">
                    <IconButton onClick={handleMenuOpen}>
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    {selectedChat.status === 'active' && (
                      <MenuItem onClick={openReassignDialog}>Reassign Chat</MenuItem>
                    )}
                    {selectedChat.status === 'active' && (
                      <MenuItem onClick={handleCloseChat}>Close Chat</MenuItem>
                    )}
                    <MenuItem onClick={refreshSelectedChat}>Refresh Chat</MenuItem>
                  </Menu>
                </Box>
              </Box>
              
              {/* Chat Messages */}
              <ChatMessages>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : selectedChat.messages?.length > 0 ? (
                  selectedChat.messages.map((msg, index) => (
                    msg.sender_type === 'system' ? (
                      <SystemMessage key={index}>
                        {msg.message}
                      </SystemMessage>
                    ) : (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography 
                          variant="caption" 
                          display="block" 
                          sx={{ 
                            textAlign: msg.sender_type === 'admin' ? 'right' : 'left',
                            mb: 0.5
                          }}
                        >
                          {msg.sender} • {formatTime(msg.created_at)}
                        </Typography>
                        <Fade in={true} timeout={msg.isNew ? 800 : 0}>
                          <MessageBubble variant={msg.sender_type === 'admin' ? 'admin' : 'visitor'} status={msg.status}>
                          <Typography variant="body2">
                            {msg.message}
                          </Typography>
                        </MessageBubble>
                        </Fade>
                      </Box>
                    )
                  ))
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2" color="textSecondary">
                      No messages in this chat
                    </Typography>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </ChatMessages>
              
              {/* Chat Input */}
              {selectedChat.status === 'active' && (
                <ChatInput component="form" onSubmit={handleSendMessage}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder="Type your message here..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={selectedChat.status !== 'active'}
                    sx={{ mr: 1 }}
                  />
                  <IconButton 
                    color="primary" 
                    type="submit"
                    disabled={!message.trim() || selectedChat.status !== 'active'}
                  >
                    <SendIcon />
                  </IconButton>
                </ChatInput>
              )}
              
              {/* Chat controls when closed */}
              {selectedChat.status !== 'active' && (
                <Box sx={{ p: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    This chat is closed
                  </Typography>
                </Box>
              )}
            </ChatContainer>
          ) : (
            <Paper sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <InfoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6">
                  Select a chat to view the conversation
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Choose a chat from the list on the left to start managing conversations.
                </Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
      
      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onClose={() => setReassignDialogOpen(false)}>
        <DialogTitle>Reassign Chat</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Select an admin to reassign this chat to:
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Admin</InputLabel>
            <Select
              value={selectedAdmin}
              onChange={(e) => setSelectedAdmin(e.target.value)}
              label="Admin"
            >
              {admins.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>
                  {admin.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReassignChat} 
            color="primary"
            disabled={!selectedAdmin}
          >
            Reassign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveChatAdmin; 